import { Visit } from '../types/visit';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { ActiveVisitDialog } from './ActiveVisitDialog';
import { VisitDetailsDialog } from './VisitDetailsDialog';
import { PrintVisitDialog } from './PrintVisitDialog';

interface VisitListProps {
  date: Date;
  visits: Visit[];
  isLoading: boolean;
}

const statusStyles = {
  scheduled: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  'in-progress': 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
  completed: 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
  cancelled: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700',
};

export function VisitList({ visits, isLoading }: VisitListProps) {
  const [activeVisit, setActiveVisit] = useState<Visit | null>(null);
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);
  const [printVisit, setPrintVisit] = useState<Visit | null>(null);

  if (isLoading) {
    return <div className="text-foreground">Loading visits...</div>;
  }

  if (!visits.length) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No visits scheduled for this day</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {visits.map((visit) => (
          <div
            key={visit.id}
            className={cn(
              "flex items-center gap-4 p-4 rounded-lg border",
              visit.type === 'outreach' 
                ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-900 dark:text-purple-100' 
                : 'bg-card text-card-foreground'
            )}
          >
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <span className="font-medium text-foreground">
                  {format(new Date(visit.startTime), 'h:mm a')}
                </span>
                <Badge variant="outline" className={statusStyles[visit.status]}>
                  {visit.status}
                </Badge>
                {visit.type === 'outreach' && (
                  <Badge variant="secondary" className="dark:bg-purple-800 dark:text-purple-100">
                    Outreach
                  </Badge>
                )}
              </div>
              <h3 className="font-medium mt-1 text-foreground">{visit.title}</h3>
            </div>
            
            <div className="flex gap-2">
              {visit.status === 'scheduled' && (
                <Button 
                  size="sm"
                  onClick={() => setActiveVisit({ ...visit, status: 'in-progress' })}
                >
                  Start Visit
                </Button>
              )}
              {visit.status === 'in-progress' && (
                <Button 
                  size="sm"
                  onClick={() => setActiveVisit(visit)}
                >
                  Continue Visit
                </Button>
              )}
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setSelectedVisit(visit)}
              >
                View Details
              </Button>
              {visit.status === 'completed' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPrintVisit(visit)}
                >
                  Print
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {activeVisit && (
        <ActiveVisitDialog
          visit={activeVisit}
          open={!!activeVisit}
          onOpenChange={(open) => !open && setActiveVisit(null)}
        />
      )}

      {selectedVisit && (
        <VisitDetailsDialog
          visit={selectedVisit}
          open={!!selectedVisit}
          onOpenChange={(open) => !open && setSelectedVisit(null)}
        />
      )}

      {printVisit && (
        <PrintVisitDialog
          visit={printVisit}
          open={!!printVisit}
          onOpenChange={(open) => !open && setPrintVisit(null)}
        />
      )}
    </>
  );
}
